import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../Layout';
import aoAPI from '../../api/aoAPI';
import ClientSearchSelect from './ClientSearchSelect';
import { FunnelIcon } from '@heroicons/react/24/outline';

const TAX_TYPE_OPTIONS = [
  'PT','VAT','WE','Excise','DST','ITR','PF',
  'SEC','BIR','BusinessPermit','TaxClearance','PhilGeps','Notarial','DocStamp','OtherExpenses',
];

const MONTHS = [
  { value: '', label: 'All Months' },
  { value: '1', label: 'January' },   { value: '2', label: 'February' },
  { value: '3', label: 'March' },     { value: '4', label: 'April' },
  { value: '5', label: 'May' },       { value: '6', label: 'June' },
  { value: '7', label: 'July' },      { value: '8', label: 'August' },
  { value: '9', label: 'September' }, { value: '10', label: 'October' },
  { value: '11', label: 'November' }, { value: '12', label: 'December' },
];

const MONTH_NAMES = ['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const fmt = (n) => n ? new Intl.NumberFormat('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n) : '—';

const StatusBadge = ({ isPaid, amount, onClick }) => {
  if (!amount && amount !== 0) {
    return <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-400">Not Recorded</span>;
  }
  if (isPaid) {
    return (
      <button
        onClick={onClick}
        className="inline-block px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700 hover:bg-green-200 transition-colors cursor-pointer"
        title="Click to mark as Pending"
      >
        ✔ Paid
      </button>
    );
  }
  return (
    <button
      onClick={onClick}
      className="inline-block px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors cursor-pointer"
      title="Click to mark as Paid"
    >
      ⏳ Pending
    </button>
  );
};

const AOCollectionMonitor = () => {
  const [clients, setClients]     = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [staffList, setStaffList]   = useState([]);
  const [fiscalYear, setFiscalYear] = useState(new Date().getFullYear());
  const [month,      setMonth]      = useState('');
  const [quarter,    setQuarter]    = useState('');
  const [taxType,    setTaxType]    = useState('');
  const [inCharge,   setInCharge]   = useState('');
  const [report,     setReport]     = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');

  useEffect(() => {
    aoAPI.getClients().then(r => setClients(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    aoAPI.getStaff().then(r => setStaffList(r.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = { fiscalYear };
      if (month)     params.month    = month;
      if (quarter)   params.quarter  = quarter;
      if (taxType)   params.taxType  = taxType;
      if (inCharge)  params.inCharge = inCharge;
      const res = await aoAPI.getCollectionStatus(params);
      setReport(res.data);
    } catch (e) {
      setError(typeof e === 'string' ? e : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [fiscalYear, month, quarter, taxType, inCharge]);

  useEffect(() => { load(); }, [load]);

  const togglePaid = async (row) => {
    try {
      await aoAPI.updateTransaction(row._id, { isPaid: !row.isPaid, paidDate: !row.isPaid ? new Date().toISOString() : null });
      await load();
    } catch (e) {
      alert(typeof e === 'string' ? e : 'Update failed');
    }
  };

  const rows = report?.rows || [];
  const visibleRows = selectedClient
    ? rows.filter(r => String(r.clientId) === String(selectedClient))
    : rows;
  const summ = {
    totalAmountDue: visibleRows.reduce((s, r) => s + (r.amount || 0), 0),
    totalCollected: visibleRows.filter(r => r.isPaid).reduce((s, r) => s + (r.amount || 0), 0),
    totalPending: visibleRows.filter(r => !r.isPaid).reduce((s, r) => s + (r.amount || 0), 0),
  };

  return (
    <Layout pageTitle="Collection Monitor">
      <div className="p-6 max-w-6xl mx-auto">
        <div className="mb-5">
          <h2 className="text-xl font-bold text-gray-800">Collection Monitor</h2>
          <p className="text-xs text-gray-500 mt-0.5">Track payment status per client, tax type, and month.</p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-end mb-5">
          <div className="min-w-[280px] flex-1 max-w-md">
            <label className="block text-xs font-medium text-gray-600 mb-1">Client</label>
            <ClientSearchSelect
              clients={clients}
              value={selectedClient}
              onChange={(id) => { setSelectedClient(id); }}
              placeholder="— Search client —"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Fiscal Year</label>
            <input
              type="number"
              value={fiscalYear}
              onChange={e => setFiscalYear(parseInt(e.target.value, 10))}
              min={2000} max={2100}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-28 focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              <FunnelIcon className="inline h-3 w-3 mr-1" />Month
            </label>
            <select
              value={month}
              onChange={e => { setMonth(e.target.value); if (e.target.value) setQuarter(''); }}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Quarter</label>
            <select
              value={quarter}
              onChange={e => { setQuarter(e.target.value); if (e.target.value) setMonth(''); }}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="">All Quarters</option>
              <option value="1">Q1</option>
              <option value="2">Q2</option>
              <option value="3">Q3</option>
              <option value="4">Q4</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Tax Type</label>
            <select
              value={taxType}
              onChange={e => setTaxType(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="">All Types</option>
              {TAX_TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">In-Charge</label>
            <select
              value={inCharge}
              onChange={e => setInCharge(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="">All Staff</option>
              {staffList.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
            </select>
          </div>
        </div>

        {/* Summary strip */}
        {summ && (
          <div className="flex flex-wrap gap-4 mb-5">
            {[
              { label: 'Total Records',    value: visibleRows.length,               bg: 'bg-gray-50' },
              { label: 'Total Amount Due', value: `₱${fmt(summ.totalAmountDue)}`,  bg: 'bg-blue-50' },
              { label: 'Total Collected',  value: `₱${fmt(summ.totalCollected)}`,  bg: 'bg-green-50' },
              { label: 'Total Pending',    value: `₱${fmt(summ.totalPending)}`,    bg: 'bg-amber-50' },
            ].map(s => (
              <div key={s.label} className={`${s.bg} border border-gray-200 rounded-lg px-4 py-2 text-center min-w-[140px]`}>
                <p className="text-xs text-gray-500">{s.label}</p>
                <p className="font-semibold text-gray-800 text-sm mt-0.5">{s.value}</p>
              </div>
            ))}
          </div>
        )}

        {error && <p className="text-sm text-red-500 mb-4">{error}</p>}

        {loading ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : visibleRows.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-12">No records found for the selected filters.</p>
        ) : (
          <div className="overflow-hidden rounded-xl border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">Client</th>
                  <th className="px-4 py-3 text-left">In-Charge</th>
                  <th className="px-4 py-3 text-center">Tax Type</th>
                  <th className="px-4 py-3 text-center">Month</th>
                  <th className="px-4 py-3 text-right">Amount</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 text-center">Paid Date</th>
                  <th className="px-4 py-3 text-left">Remarks</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {visibleRows.map(r => (
                  <tr key={r._id} className={r.isPaid ? 'bg-green-50' : r.amount > 0 ? 'bg-amber-50' : 'hover:bg-gray-50'}>
                    <td className="px-4 py-2.5 font-medium text-gray-800">
                      {r.clientName}
                      {r.clientCode && <span className="text-gray-400 font-mono text-xs ml-1">({r.clientCode})</span>}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600">{r.inCharge?.name || '—'}</td>
                    <td className="px-4 py-2.5 text-center">
                      <span className="inline-block bg-gray-100 text-gray-700 rounded px-2 py-0.5 text-xs font-mono font-semibold">
                        {r.taxType}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-center text-gray-600">{MONTH_NAMES[r.month] || r.month}</td>
                    <td className="px-4 py-2.5 text-right font-mono font-medium text-gray-800">₱{fmt(r.amount)}</td>
                    <td className="px-4 py-2.5 text-center">
                      <StatusBadge isPaid={r.isPaid} amount={r.amount} onClick={() => togglePaid(r)} />
                    </td>
                    <td className="px-4 py-2.5 text-center text-gray-500 text-xs">
                      {r.paidDate ? new Date(r.paidDate).toLocaleDateString('en-PH') : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-gray-400 text-xs truncate max-w-[180px]">{r.remarks || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AOCollectionMonitor;
